<template>
  <div class="chat-wrapper-premium">
    <CardIeaAppMain>
      <v-col cols="12" class="pa-0">
        <!-- Hero Header -->
        <div v-if="!$vuetify.breakpoint.smAndDown || (!chatActive && !chatGptActive)" class="hero-header glass-panel mb-4 py-3 px-6 mx-4 mt-4">
          <div class="d-flex align-center justify-space-between w-100">
            <div class="d-flex align-center">
              <v-btn icon dark color="primary" class="mr-4 glass-btn" @click="$router.push('/')">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <div>
                <h1 class="text-h5 font-weight-bold primary-text mb-0">Mensajes</h1>
                <p class="text-caption grey--text text--darken-1 mb-0">Comunícate con tus compañeros y docentes</p>
              </div>
            </div>
            <v-icon size="48" color="primary" class="opacity-30">mdi-chat-processing-outline</v-icon>
          </div>
        </div>

        <!-- Service Inactive Alert -->
        <div v-if="active == 0" class="pa-4">
          <v-alert
            prominent
            type="info"
            class="rounded-xl glass-card border-none"
          >
            Servicio temporalmente inactivo
          </v-alert>
        </div>

        <v-row
          v-else
          class="chat-layout-container ma-0"
          :class="{
            'mobile-view': $vuetify.breakpoint.smAndDown,
            'has-active-chat': chatActive || chatGptActive
          }"
          :style="{
            height: ($vuetify.breakpoint.smAndDown && (chatActive || chatGptActive))
              ? 'calc(100vh - 110px) !important'
              : 'calc(100vh - 190px) !important',
            overflow: 'hidden',
            display: 'flex',
            flexWrap: 'nowrap'
          }"
        >
          
          <!-- SIDEBAR: Chats & Contacts -->
          <v-col
            cols="12"
            md="4"
            class="sidebar-glass pa-0 d-flex flex-column border-right"
            style="height: 100%; overflow: hidden;"
          >
            
            <!-- Sidebar Header: Tabs -->
            <div class="sidebar-header pa-4 pb-0">
              <v-tabs
                v-model="tab"
                grow
                color="primary"
                class="premium-tabs"
                slider-size="3"
              >
                <v-tab href="#chat">
                  <v-icon left small>mdi-chat-processing-outline</v-icon>
                  CHATS
                </v-tab>
                <v-tab href="#contacts">
                  <v-icon left small>mdi-account-group-outline</v-icon>
                  DIRECTORIO
                </v-tab>
              </v-tabs>
            </div>

            <!-- Sidebar Search -->
            <div class="px-4 py-3">
              <v-text-field
                v-model="searchQuery"
                :placeholder="tab === 'chat' ? 'Buscar conversaciones...' : 'Buscar en el directorio...'"
                prepend-inner-icon="mdi-magnify"
                outlined
                rounded
                dense
                hide-details
                class="premium-search-input"
                :append-icon="searchQuery ? 'mdi-close' : ''"
                @click:append="searchQuery = ''"
                @keyup.enter="handleSearch"
              />
            </div>

            <!-- Sidebar Content: Lists -->
            <div
              class="flex-grow-1 overflow-y-auto sidebar-content-scroll"
              ref="sidebarScroll"
              @scroll="handleSidebarScroll"
            >
              <v-tabs-items v-model="tab" class="transparent">
                
                <!-- TAB: RECENT CHATS -->
                <v-tab-item value="chat">
                  <div v-if="loadingChats && !chats.length" class="text-center pa-10">
                    <v-progress-circular indeterminate color="primary" />
                  </div>
                  
                  <v-list v-else-if="chats.length" class="pa-0 transparent">
                    <v-list-item
                      v-for="chat in filteredChats"
                      :key="'chat-' + chat.id"
                      @click="selectChat(chat)"
                      :class="['chat-list-item', { 'active-chat': chatActive && chatActive.id === chat.id }]"
                    >
                      <v-list-item-avatar size="50" class="elevation-1">
                        <v-img :src="chat.image" v-if="chat.image" />
                        <v-icon v-else color="primary lighten-3">
                          {{ chat.type === 'Group' ? 'mdi-account-multiple' : 'mdi-account' }}
                        </v-icon>
                      </v-list-item-avatar>

                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">{{ chat.name }}</v-list-item-title>
                        <v-list-item-subtitle class="text-truncate">
                          <span v-if="chat.last_message">
                            <strong v-if="chat.type === 'Group'">{{ chat.last_message_sender_name }}: </strong>
                            {{ chat.last_message.message }}
                          </span>
                          <span v-else class="grey--text italic">No hay mensajes</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>

                      <v-list-item-action class="align-self-start mt-2">
                        <small class="grey--text">{{ chat.last_message_time }}</small>
                        <v-badge v-if="chat.notifications > 0" :content="chat.notifications" color="error" overlap />
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>

                  <div v-else class="text-center pa-10 grey--text">
                    <v-icon size="48" color="grey lighten-2">mdi-chat-sleep-outline</v-icon>
                    <p class="mt-2">No tienes chats activos</p>
                  </div>
                </v-tab-item>

                <!-- TAB: CONTACT DIRECTORY -->
                <v-tab-item value="contacts">
                  <div v-if="loadingContacts && !Object.keys(contacts).length" class="text-center pa-10">
                    <v-progress-circular indeterminate color="primary" />
                  </div>

                  <div v-else-if="Object.keys(contactsFiltered).length">
                    <div v-for="(group, letter) in contactsFiltered" :key="'group-' + letter">
                      <div class="contact-divider px-4 py-1 font-weight-bold grey--text text-caption">
                        {{ letter }}
                      </div>
                      <v-list class="pa-0 transparent">
                        <v-list-item
                          v-for="person in group"
                          :key="'contact-' + person.IDStB"
                          @click="startChatWithContact(person)"
                          class="chat-list-item"
                        >
                          <v-list-item-avatar size="40" class="elevation-1">
                            <v-img :src="person.imagen" v-if="person.imagen" />
                            <v-icon v-else color="primary lighten-3">mdi-account-outline</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{ person.Nombres }}</v-list-item-title>
                            <v-list-item-subtitle>{{ person.tipo_usuario | capitalize }}</v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-icon small color="primary lighten-2">mdi-message-text-outline</v-icon>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>
                    </div>
                  </div>

                  <div v-else class="text-center pa-10 grey--text">
                    <v-icon size="48" color="grey lighten-2">mdi-account-search-outline</v-icon>
                    <p class="mt-2">No se encontraron contactos</p>
                  </div>
                </v-tab-item>
              </v-tabs-items>
            </div>

            <!-- New Group Button (Only for Contacts Tab) -->
            <div class="pa-4 border-top" v-if="tab === 'contacts'">
              <v-btn block color="primary" rounded depressed @click="showGroupNew = true">
                <v-icon left>mdi-plus</v-icon>
                NUEVO GRUPO
              </v-btn>
            </div>
          </v-col>

          <!-- MAIN CONTENT: Active Chat -->
          <v-col
            cols="12"
            md="8"
            class="pa-0 d-flex flex-column main-chat-area"
            style="height: 100%; overflow: hidden;"
          >
            
            <div class="flex-grow-1 d-flex flex-column" style="height: 0; min-height: 0;">
              <v-fade-transition mode="out-in" @after-enter="scrollToBottom">
                <!-- Case: Chat GPT Active -->
                <div v-if="chatGptActive" key="gpt" class="fill-height d-flex flex-column">
                  <div class="chat-header pa-4 border-bottom glass-header d-flex align-center">
                    <v-btn
                      v-if="$vuetify.breakpoint.smAndDown"
                      icon
                      color="primary"
                      class="mr-2"
                      @click="closeActiveChat"
                    >
                      <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-avatar size="45" class="elevation-2 mr-3">
                      <v-img src="@/assets/logo-header.svg" />
                    </v-avatar>
                    <div class="font-weight-bold primary--text">Asistente IEA (GPT-3)</div>
                  </div>
                  <div class="flex-grow-1 overflow-y-auto pa-4 bg-light">
                    <Chatgpt :autoMessage="true" />
                  </div>
                </div>

                <!-- Case: Active Chat Selected -->
                <div v-else-if="chatActive" :key="'chat-' + chatActive.id" class="fill-height d-flex flex-column">
                  
                  <!-- Chat Header -->
                  <div class="chat-header pa-4 border-bottom glass-header d-flex align-center z-index-2">
                    <v-btn
                      v-if="$vuetify.breakpoint.smAndDown"
                      icon
                      color="primary"
                      class="mr-2"
                      @click="closeActiveChat"
                    >
                      <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-avatar size="45" color="primary lighten-4" class="elevation-2 cursor-pointer" @click="openProfile(chatActive.other_alumno)">
                      <v-img :src="chatActive.image" v-if="chatActive.image" />
                      <v-icon v-else>{{ chatActive.type === 'Group' ? 'mdi-account-multiple' : 'mdi-account' }}</v-icon>
                    </v-avatar>
                    
                    <div class="ml-4" style="min-width: 0; flex: 1;">
                      <div class="text-subtitle-1 font-weight-bold primary--text mb-0 truncate" style="max-width: 100%;">{{ chatActive.name }}</div>
                      <div class="text-caption grey--text d-flex align-center">
                        <v-icon x-small color="success" class="mr-1">mdi-circle</v-icon>
                        Activo ahora
                      </div>
                    </div>

                    <v-spacer />

                    <!-- Group Actions -->
                    <v-menu v-if="chatActive.type === 'Group'" offset-y transition="slide-y-transition">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon v-bind="attrs" v-on="on" class="glass-btn">
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list dense class="rounded-lg">
                        <v-list-item @click="showGroup = true">
                          <v-list-item-icon class="mr-2"><v-icon small>mdi-account-group</v-icon></v-list-item-icon>
                          <v-list-item-title>Información del grupo</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="showReporte = true">
                          <v-list-item-icon class="mr-2"><v-icon small color="error">mdi-alert-octagon</v-icon></v-list-item-icon>
                          <v-list-item-title class="error--text">Reportar grupo</v-list-item-title>
                        </v-list-item>
                        <v-divider />
                        <v-list-item @click="leaveChat()" :disabled="chatActive.can_exit == 0">
                          <v-list-item-icon class="mr-2"><v-icon small>mdi-logout</v-icon></v-list-item-icon>
                          <v-list-item-title>Salir del grupo</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>

                  <!-- Messages Area -->
                  <div class="flex-grow-1 overflow-y-auto pa-6 messages-viewport" ref="chatMessages" @scroll="handleChatScroll">
                    <div v-for="day in groupedMessages" :key="day.day">
                      <div class="d-flex justify-center my-6">
                        <v-chip small class="date-chip elevation-0 px-4">{{ day.day }}</v-chip>
                      </div>

                      <div v-for="msg in day.messages" :key="msg.id" :class="['message-row d-flex mb-4', msg.user_id == user.IDStB ? 'justify-end' : 'justify-start']">
                        
                        <!-- Message Avatar (Only for Group incoming) -->
                        <v-avatar size="30" v-if="msg.user_id != user.IDStB && chatActive.type === 'Group'" class="mr-2 mt-1">
                          <v-img :src="msg.alumno.imagen" v-if="msg.alumno && msg.alumno.imagen" />
                          <v-icon small v-else>mdi-account</v-icon>
                        </v-avatar>

                        <div class="message-bubble-wrapper" :style="{ maxWidth: '75%' }">
                          <div v-if="msg.user_id != user.IDStB && chatActive.type === 'Group'" class="text-caption primary--text font-weight-bold ml-2 mb-1">
                            {{ msg.alumno ? msg.alumno.Nombres : "Desconocido" }}
                          </div>
                          
                          <v-card :class="['pa-3 elevation-1', msg.user_id == user.IDStB ? 'bubble-out' : 'bubble-in']">
                            <!-- Text Content -->
                            <div v-if="msg.message" v-html="formatMessage(msg)" class="message-text"></div>
                            
                            <!-- File Content -->
                            <div v-if="msg.file" class="mt-2 file-attachment">
                              <a :href="msg.file" target="_blank" class="d-flex align-center text-decoration-none">
                                <v-icon color="primary" class="mr-2">mdi-file-document-outline</v-icon>
                                <span class="text-caption font-weight-bold truncate">{{ msg.filename || 'Archivo adjunto' }}</span>
                              </a>
                            </div>

                            <div class="d-flex justify-end mt-1">
                              <small class="msg-time opacity-7">{{ msg.created_at.substr(11, 5) }}</small>
                              <v-icon v-if="msg.user_id == user.IDStB" x-small class="ml-1" color="primary lighten-3">mdi-check-all</v-icon>
                            </div>
                          </v-card>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Input Footer -->
                  <div class="chat-footer pa-4 border-top glass-footer z-index-2">
                    <!-- Emoji Picker (Absolute) -->
                    <div v-if="showEmojis" class="emoji-picker-container">
                      <VEmojiPicker @select="onEmojiSelect" />
                    </div>

                    <v-row no-gutters align="center">
                      <v-col class="flex-grow-1">
                        <Mentionable
                          :keys="['@']"
                          :items="chatActive.participants || []"
                          offset="6"
                          @apply="onMentionSelect"
                        >
                          <v-text-field
                            v-model="messageForm.content"
                            placeholder="Escribe un mensaje..."
                            outlined
                            rounded
                            hide-details
                            class="premium-input"
                            background-color="#f8fafc"
                            :disabled="sendingMessage"
                            @keyup.enter="sendMessage"
                          >
                            <template v-slot:prepend-inner>
                              <v-btn icon small @click="showEmojis = !showEmojis" :color="showEmojis ? 'primary' : 'grey'">
                                <v-icon>mdi-emoticon-outline</v-icon>
                              </v-btn>
                            </template>
                            <template v-slot:append>
                              <v-btn icon small color="grey" @click="triggerFileUpload">
                                <v-icon>mdi-paperclip</v-icon>
                              </v-btn>
                            </template>
                          </v-text-field>
                        </Mentionable>
                      </v-col>
                      <v-col cols="auto" class="pl-3 d-flex align-center">
                        <v-btn
                          color="primary"
                          dark
                          fab
                          small
                          depressed
                          :loading="sendingMessage"
                          :disabled="!messageForm.content && !messageForm.files.length"
                          @click="sendMessage"
                        >
                          <v-icon small>mdi-send</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>
                </div>

                <!-- Case: Empty State -->
                <div v-else key="empty" class="fill-height d-flex flex-column align-center justify-center text-center pa-12 bg-light-gradient">
                  <v-icon size="120" color="primary lighten-4" class="mb-6 animate-float">mdi-chat-processing-outline</v-icon>
                  <h2 class="text-h4 font-weight-bold grey--text text--darken-2 mb-4">Tus Mensajes</h2>
                  <p class="text-h6 grey--text text--darken-1" style="max-width: 500px">
                    Selecciona una conversación de la lista o busca a alguien en el directorio para comenzar a chatear.
                  </p>
                  <v-btn color="primary" outlined rounded large class="mt-4" @click="tab = 'contacts'">
                    VER DIRECTORIO
                  </v-btn>
                </div>
              </v-fade-transition>
            </div>

          </v-col>
        </v-row>

        <!-- Hidden File Input -->
        <input type="file" ref="fileInput" class="d-none" multiple @change="onFilesSelected" />

        <!-- MODALS & DIALOGS -->
        <ModalCustom :show="showAlert" @update="showAlert = $event" :content="alertContent" :type="alertType" />
        
        <v-dialog v-model="showGroupNew" max-width="500px" scrollable>
          <v-card class="rounded-xl">
            <v-card-title class="primary white--text">Crear nuevo grupo</v-card-title>
            <v-card-text class="pa-4">
              <v-text-field v-model="newGroupName" label="Nombre del grupo" outlined dense rounded class="mt-2" />
              <div class="font-weight-bold mb-2">Selecciona participantes:</div>
              <v-list dense class="max-height-300 overflow-y-auto">
                <v-list-item v-for="person in flatContacts" :key="person.IDStB">
                  <v-list-item-action><v-checkbox v-model="selectedForGroup" :value="person" /></v-list-item-action>
                  <v-list-item-avatar size="32"><v-img :src="person.imagen" v-if="person.imagen" /></v-list-item-avatar>
                  <v-list-item-content><v-list-item-title>{{ person.Nombres }}</v-list-item-title></v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-card-actions class="pa-4 border-top">
              <v-btn text @click="showGroupNew = false">Cancelar</v-btn>
              <v-spacer />
              <v-btn color="primary" rounded depressed @click="createNewGroup" :disabled="!newGroupName || selectedForGroup.length < 1">Crear Grupo</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Group Info Modal -->
        <v-dialog v-model="showGroup" max-width="600px" scrollable v-if="chatActive && chatActive.type === 'Group'">
          <v-card class="rounded-xl">
            <v-card-title>Información del Grupo</v-card-title>
            <v-divider />
            <v-card-text style="height: 400px" class="pa-0">
              <v-list subheader>
                <v-subheader>Participantes ({{ chatActive.participants ? chatActive.participants.length : 0 }})</v-subheader>
                <v-list-item v-for="p in chatActive.participants" :key="p.id">
                  <v-list-item-avatar><v-img :src="p.alumno ? p.alumno.imagen : p.profesor.empleado.imagen" v-if="(p.alumno && p.alumno.imagen) || (p.profesor && p.profesor.empleado.imagen)" /></v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ p.alumno ? p.alumno.Nombres : p.profesor.empleado.nombres }}</v-list-item-title>
                    <v-list-item-subtitle v-if="p.administrator"><v-chip x-small color="success" outlined>Admin</v-chip></v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4">
              <v-btn block color="error" text @click="leaveChat">Salir del grupo</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Attachment Preview Snackbar -->
        <v-snackbar v-model="showAttachPreview" :timeout="-1" bottom class="attach-snackbar">
          <div class="d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-file-upload</v-icon>
            {{ messageForm.files.length }} archivo(s) seleccionados
            <v-spacer />
            <v-btn text color="error" small @click="clearFiles">Cancelar</v-btn>
          </div>
        </v-snackbar>

      </v-col>
    </CardIeaAppMain>
  </div>
</template>

<script>
import CardIeaAppMain from "../../components/CardIeaAppMain";
import ModalCustom from "../../components/ModalCustom";
import Chatgpt from "../../components/Chatgpt";
import moment from "moment";
import SocketConnection from "itoeste-socket";
import { DEBUG, API_SOCKET } from "../../../env";
import { VEmojiPicker } from "v-emoji-picker";
import { Mentionable } from "vue-mention";

moment.locale("es");

export default {
  name: "Chat",
  components: {
    CardIeaAppMain,
    ModalCustom,
    Chatgpt,
    VEmojiPicker,
    Mentionable
  },
  filters: {
    capitalize(value) {
      if (!value) return "";
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
  },
  props: ["chatgpt", "showInfo"],
  data: () => ({
    // UI State
    tab: "chat",
    searchQuery: "",
    active: 1, // Service status
    loadingChats: false,
    loadingContacts: false,
    loadingMessages: false,
    sendingMessage: false,
    showEmojis: false,
    showAttachPreview: false,
    
    // Data
    chats: [],
    contacts: {}, // Grouped by letter
    chatActive: null,
    chatGptActive: null,
    ws: null,
    
    // Forms
    messageForm: {
      content: "",
      files: []
    },
    newGroupName: "",
    selectedForGroup: [],
    
    // Pagination
    chatsPage: 1,
    chatsLastPage: 1,
    contactsPage: 1,
    
    // Modals
    showAlert: false,
    alertContent: "",
    alertType: "modal-success",
    showGroup: false,
    showGroupNew: false,
    showReporte: false,
    chatgptAlumnoActive: 0
  }),

  computed: {
    user() {
      return this.$store.getters.user;
    },
    contactsFiltered() {
      if (!this.searchQuery || this.tab !== 'contacts') return this.contacts;
      
      const filtered = {};
      const q = this.searchQuery.toUpperCase();
      
      Object.keys(this.contacts).forEach(letter => {
        const group = this.contacts[letter].filter(p => 
          (p.Nombres || "").toUpperCase().includes(q)
        );
        if (group.length) filtered[letter] = group;
      });
      return filtered;
    },
    filteredChats() {
      if (!this.searchQuery || this.tab !== 'chat') return this.chats;
      const q = this.searchQuery.toUpperCase();
      return this.chats.filter(c => (c.name || "").toUpperCase().includes(q));
    },
    flatContacts() {
      return Object.values(this.contacts).flat();
    },
    groupedMessages() {
      if (!this.chatActive || !this.chatActive.messages) return [];
      return this.chatActive.messages;
    }
  },

  watch: {
    tab(newVal) {
      this.searchQuery = "";
      if (newVal === "chat") {
        this.contacts = {}; // Clear contacts when moving to chat
        this.loadChats(true);
      } else if (newVal === "contacts") {
        this.chats = []; // Clear chats when moving to contacts
        this.loadContacts(true);
      }
    },
    groupedMessages: {
      handler() {
        this.scrollToBottom();
      },
      deep: true
    }
  },

  mounted() {
    this.initService();
    this.initSocket();
    this.loadChats(true);
    this.checkChatGptStatus();
    
    const { id, userid } = this.$route.params;
    if (id) this.fetchChatById(id);
    else if (userid) this.openDirectChat(userid);
  },

  methods: {
    // --- INITIALIZATION ---
    async initService() {
      try {
        const res = await this.$API.get({ url: "chats-service-status" });
        this.active = res;
      } catch (e) {
        console.error("Service status check failed", e);
      }
    },

    initSocket() {
      this.ws = SocketConnection({
        debug: DEBUG,
        token: API_SOCKET,
        user: this.user.IDStB
      }, () => {});

      this.ws.subscribeChannel(`chat-${this.user.IDStB}`, (event) => {
        this.handleSocketEvent(event);
      });
    },

    // --- DATA LOADING ---
    async loadChats(reload = false) {
      if (reload) {
        this.chatsPage = 1;
        this.chats = [];
      } else {
        if (this.chatsPage >= this.chatsLastPage) return;
        this.chatsPage++;
      }

      this.loadingChats = true;
      try {
        const res = await this.$API.get({ url: `chats?page=${this.chatsPage}` });
        const data = res.data || res;
        const items = Array.isArray(data) ? data : Object.values(data).flat();
        
        // Process and filter duplicates
        const processed = items
          .map((chat) => this.processChatItem(chat))
          .filter(
            (chat, index, self) =>
              index === self.findIndex((t) => t.id === chat.id) &&
              !this.chats.some((c) => c.id === chat.id)
          );

        if (reload) {
          this.chats = items
            .map((chat) => this.processChatItem(chat))
            .filter((chat, index, self) => index === self.findIndex((t) => t.id === chat.id))
            .sort((a, b) => new Date(b.last_message.created_at || 0) - new Date(a.last_message.created_at || 0));
        } else {
          this.chats.push(...processed);
          this.chats.sort((a, b) => new Date(b.last_message.created_at || 0) - new Date(a.last_message.created_at || 0));
        }
        
        this.chatsLastPage = res.last_page || 1;
      } catch (e) {
        console.error("Load chats error", e);
      } finally {
        this.loadingChats = false;
      }
    },

    async loadContacts(reload = true) {
      if (reload) {
        this.contactsPage = 1;
        this.contacts = {};
      } else {
        this.contactsPage++;
      }

      this.loadingContacts = true;
      try {
        const q = this.searchQuery && this.tab === 'contacts' ? `&search=${this.searchQuery}` : "";
        const res = await this.$API.get({ url: `contacts?paginate=1&page=${this.contactsPage}${q}` });
        const data = res.data || res;
        
        if (reload) {
          // Ensure each person only appears once in the entire object
          const seen = new Set();
          const uniqueData = {};
          Object.keys(data).forEach((letter) => {
            const filtered = data[letter].filter((p) => {
              if (seen.has(p.IDStB)) return false;
              seen.add(p.IDStB);
              return true;
            });
            if (filtered.length) uniqueData[letter] = filtered;
          });
          this.contacts = uniqueData;
        } else {
          Object.keys(data).forEach((letter) => {
            const filteredNew = data[letter].filter((p) => {
              // Check if person exists in ANY group
              return !Object.values(this.contacts).some((group) =>
                group.some((ex) => ex.IDStB === p.IDStB)
              );
            });

            if (filteredNew.length) {
              if (this.contacts[letter]) {
                this.contacts[letter].push(...filteredNew);
              } else {
                this.$set(this.contacts, letter, filteredNew);
              }
            }
          });
        }
      } catch (e) {
        console.error("Load contacts error", e);
      } finally {
        this.loadingContacts = false;
      }
    },

    processChatItem(chat) {
      if (chat.type === 'Individual') {
        const other = chat.participants.find(p => p.user_id !== this.user.IDStB);
        if (other) {
          const profile = other.alumno || other.profesor.empleado;
          chat.name = profile.Nombres || (profile.nombres + ' ' + profile.apellidos);
          chat.image = profile.imagen;
          chat.other_alumno = profile.IDStB || other.user_id;
        }
      }
      chat.last_message_time = chat.last_message ? chat.last_message.created_at.substr(11, 5) : "";
      return chat;
    },

    // --- CHAT ACTIONS ---
    async selectChat(chat) {
      this.chatGptActive = null;
      this.chatActive = chat;
      this.loadingMessages = true;
      
      try {
        const res = await this.$API.get({ 
          url: `chats/messages/${chat.id}?page=1&per_page=30` 
        });
        const messages = (res.data || res).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        this.$set(this.chatActive, "messages", this.groupMessagesByDate(messages));
        this.chatActive.notifications = 0;
        this.$nextTick(this.scrollToBottom);
      } catch (e) {
        console.error("Get chat messages error", e);
      } finally {
        this.loadingMessages = false;
      }
    },

    startChatWithContact(person) {
      const existing = this.chats.find(c => 
        c.type === 'Individual' && 
        c.participants.some(p => p.user_id === person.IDStB)
      );
      
      if (existing) {
        this.tab = "chat";
        this.selectChat(existing);
      } else {
        this.chatActive = {
          id: null,
          name: person.Nombres,
          image: person.imagen,
          type: 'Individual',
          participants: [person],
          messages: []
        };
      }
    },

    async sendMessage() {
      if (!this.messageForm.content && !this.messageForm.files.length) return;
      if (this.sendingMessage) return;

      this.sendingMessage = true;
      const content = this.messageForm.content;
      const files = [...this.messageForm.files];
      
      try {
        if (!this.chatActive.id) {
          const newChatRes = await this.createIndividualChat(this.chatActive.participants[0]);
          this.chatActive.id = newChatRes.id;
          this.chats.unshift(this.processChatItem(newChatRes));
        }

        if (files.length) {
          for (const file of files) {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("type", this.getFileType(file));
            if (file === files[0] && content) formData.append("message", content);
            
            await this.$API.post({ url: `chats/messages/${this.chatActive.id}`, data: formData }, false);
          }
        } else {
          const formData = new FormData();
          formData.append("message", content);
          await this.$API.post({ url: `chats/messages/${this.chatActive.id}`, data: formData }, false);
        }

        this.messageForm.content = "";
        this.clearFiles();
        this.showEmojis = false;
      } catch (e) {
        console.error("Send message error", e);
        this.triggerAlert("Error al enviar el mensaje", "modal-danger");
      } finally {
        this.sendingMessage = false;
        this.scrollToBottom();
      }
    },

    async createIndividualChat(person) {
      const formData = new FormData();
      formData.append("participants[0][IDStB]", person.IDStB);
      formData.append("participants[0][typeUser]", person.tipo_usuario);
      formData.append("type", "Individual");
      return await this.$API.post({ url: "chats", data: formData }, false);
    },

    async createNewGroup() {
      if (!this.newGroupName || this.selectedForGroup.length < 1) return;
      
      const formData = new FormData();
      formData.append("name", this.newGroupName);
      formData.append("type", "Group");
      this.selectedForGroup.forEach((p, i) => {
        formData.append(`participants[${i}][IDStB]`, p.IDStB);
        formData.append(`participants[${i}][typeUser]`, p.tipo_usuario);
      });

      try {
        const res = await this.$API.post({ url: "chats", data: formData });
        this.chats.unshift(this.processChatItem(res));
        this.showGroupNew = false;
        this.newGroupName = "";
        this.selectedForGroup = [];
        this.selectChat(res);
      } catch (e) {
        this.triggerAlert("Error al crear el grupo", "modal-danger");
      }
    },

    groupMessagesByDate(messages) {
      const groups = [];
      const today = moment().format("YYYY-MM-DD");
      
      messages.forEach(msg => {
        let day = moment(msg.created_at).format("YYYY-MM-DD");
        day = (day === today) ? "HOY" : moment(msg.created_at).format("DD/MM/YYYY");
        
        let existing = groups.find(g => g.day === day);
        if (existing) existing.messages.push(msg);
        else groups.push({ day, messages: [msg] });
      });
      return groups;
    },

    formatMessage(msg) {
      return msg.message || "";
    },

    getFileType(file) {
      if (file.type.match('image.*')) return 'image';
      if (file.type.match('pdf.*')) return 'pdf';
      return 'document';
    },

    handleSearch() {
      if (this.tab === 'contacts') this.loadContacts(true);
    },

    handleSocketEvent(event) {
      console.log("Socket Event:", event);
      switch (event.action) {
        case "message-new":
          this.onNewMessage(JSON.parse(event.message));
          break;
        case "chat-new": {
          const newChat = this.processChatItem(JSON.parse(event.chat));
          if (!this.chats.some((c) => c.id === newChat.id)) {
            this.chats.unshift(newChat);
          }
          break;
        }
      }
    },

    onNewMessage(msg) {
      // 1. Update sidebar item
      const chatIndex = this.chats.findIndex(c => c.id == msg.chat_id);
      if (chatIndex !== -1) {
        const chat = this.chats.splice(chatIndex, 1)[0];
        chat.last_message = msg;
        chat.last_message_time = moment(msg.created_at).format("HH:mm");
        if (!this.chatActive || this.chatActive.id != chat.id) {
          chat.notifications = (chat.notifications || 0) + 1;
        }
        this.chats.unshift(chat);
      }

      // 2. If it's the active chat, add to messages
      if (this.chatActive && this.chatActive.id == msg.chat_id) {
        // Prevent duplicates
        const exists = this.chatActive.messages.some(group => 
          group.messages.some(m => m.id === msg.id)
        );
        if (exists) return;

        const dayLabel = this.getDayLabel(msg.created_at);
        const group = this.chatActive.messages.find(g => g.day === dayLabel);
        
        if (group) {
          group.messages.push(msg);
        } else {
          this.chatActive.messages.push({ day: dayLabel, messages: [msg] });
        }
        
        this.$nextTick(this.scrollToBottom);
      }
    },

    getDayLabel(date) {
      const today = moment().format("YYYY-MM-DD");
      const day = moment(date).format("YYYY-MM-DD");
      if (day === today) return "HOY";
      if (day === moment().subtract(1, 'days').format("YYYY-MM-DD")) return "AYER";
      return moment(date).format("DD/MM/YYYY");
    },

    scrollToBottom() {
      setTimeout(() => {
        const container = this.$refs.chatMessages;
        if (container) {
          container.scrollTop = container.scrollHeight;
          // One more time to be absolutely sure
          this.$nextTick(() => {
            container.scrollTop = container.scrollHeight;
          });
        }
      }, 300);
    },

    handleChatScroll() {
      // Pagination for messages could go here
    },

    handleSidebarScroll(e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      // Trigger load earlier (100px from bottom)
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        if (this.tab === "chat" && !this.loadingChats) {
          this.loadChats(false);
        } else if (this.tab === "contacts" && !this.loadingContacts) {
          this.loadContacts(false);
        }
      }
    },

    triggerFileUpload() { this.$refs.fileInput.click(); },
    onFilesSelected(e) { 
      this.messageForm.files = Array.from(e.target.files); 
      if (this.messageForm.files.length) this.showAttachPreview = true;
    },
    clearFiles() { this.messageForm.files = []; this.showAttachPreview = false; },
    onEmojiSelect(emoji) { this.messageForm.content += emoji.data; },
    onMentionSelect(item) {
      this.messageForm.content += `${item.value} `;
    },
    
    triggerAlert(msg, type = "modal-success") {
      this.alertContent = msg;
      this.alertType = type;
      this.showAlert = true;
    },

    openProfile(id) {
      if (id) this.$router.push({ name: "Profile", params: { id } });
    },

    async checkChatGptStatus() {
      const res = await this.$API.get({ url: "chatgpt-alumno-status" });
      this.chatgptAlumnoActive = res;
    },

    async fetchChatById(id) {
      this.loadingMessages = true;
      try {
        const chatRes = await this.$API.get({ url: `chats/${id}` });
        if (chatRes) {
          const chat = this.processChatItem(chatRes);
          if (!this.chats.some(c => c.id === chat.id)) {
            this.chats.unshift(chat);
          }
          this.tab = "chat";
          this.selectChat(chat);
        }
      } catch (e) {
        console.error("Fetch chat by id error", e);
      } finally {
        this.loadingMessages = false;
      }
    },

    async openDirectChat(userid) {
      this.loadingMessages = true;
      try {
        const existing = this.chats.find(c => 
          c.type === 'Individual' && 
          c.participants.some(p => p.user_id == userid)
        );
        
        if (existing) {
          this.tab = "chat";
          this.selectChat(existing);
          return;
        }

        const person = { IDStB: userid, tipo_usuario: 'alumno' };
        const newChatRes = await this.createIndividualChat(person);
        if (newChatRes && newChatRes.id) {
          const chat = this.processChatItem(newChatRes);
          if (!this.chats.some(c => c.id === chat.id)) {
            this.chats.unshift(chat);
          }
          this.tab = "chat";
          this.selectChat(chat);
        }
      } catch (e) {
        console.error("Open direct chat error", e);
      } finally {
        this.loadingMessages = false;
      }
    },

    closeActiveChat() {
      this.chatActive = null;
      this.chatGptActive = null;
      if (this.$route.params.id || this.$route.params.userid) {
        this.$router.push({ name: 'Chat' });
      }
    }
  }
};
</script>

<style scoped>
.chat-wrapper-premium {
  height: 100%;
  background: transparent !important;
}

.chat-layout-container {
  background: transparent !important;
  border-radius: 16px;
  overflow: hidden;
}

.sidebar-glass {
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
}

.main-chat-area {
  background: rgba(255, 255, 255, 0.2);
  position: relative;
}

.glass-header {
  background: rgba(255, 255, 255, 0.3) !important;
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.glass-footer {
  background: rgba(255, 255, 255, 0.4) !important;
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
  position: relative;
  z-index: 10;
}

.chat-list-item {
  transition: all 0.2s ease;
  border-left: 4px solid transparent;
  margin: 4px 8px;
  border-radius: 12px;
}

.chat-list-item:hover {
  background: rgba(25, 118, 210, 0.05);
}

.chat-list-item.active-chat {
  background: rgba(25, 118, 210, 0.1);
  border-left-color: #1976d2;
}

.bubble-in {
  background: white !important;
  border-radius: 18px 18px 18px 4px !important;
  color: #334155;
}

.bubble-out {
  background: #1976d2 !important;
  color: white !important;
  border-radius: 18px 18px 4px 18px !important;
}

.bubble-out .msg-time { color: rgba(255, 255, 255, 0.7); }

.date-chip {
  background: rgba(0, 0, 0, 0.05) !important;
  color: #64748b;
  font-weight: 600;
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}
.animate-float { animation: float 3s ease-in-out infinite; }

.sidebar-content-scroll::-webkit-scrollbar,
.messages-viewport::-webkit-scrollbar {
  width: 6px;
}
.sidebar-content-scroll::-webkit-scrollbar-thumb,
.messages-viewport::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
}

.emoji-picker-container {
  position: absolute;
  bottom: 80px;
  left: 20px;
  z-index: 100;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  border-radius: 12px;
  overflow: hidden;
}

@media (max-width: 600px) {
  .emoji-picker-container {
    left: 10px;
    right: 10px;
    bottom: 75px;
    width: calc(100% - 20px) !important;
  }
  .emoji-picker-container > * {
    width: 100% !important;
  }
}

.line-height-1 { line-height: 1.2; }
.truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.z-index-2 { z-index: 2; }

/* Hero Header and Glass Panel (from Denuncia) */
.glass-panel {
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(16px) !important;
  -webkit-backdrop-filter: blur(16px) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.05) !important;
  border-radius: 20px;
  padding: 24px;
}

.glass-btn {
  background: rgba(25, 118, 210, 0.1) !important;
  backdrop-filter: blur(4px) !important;
  box-shadow: none !important;
  transition: all 0.3s ease;
}
.glass-btn:hover {
  background: rgba(25, 118, 210, 0.2) !important;
  transform: translateY(-2px);
}
.opacity-30 {
  opacity: 0.3;
}

@media (max-width: 959px) {
  .chat-layout-container.mobile-view .sidebar-glass,
  .chat-layout-container.mobile-view .main-chat-area {
    width: 100% !important;
    max-width: 100% !important;
    flex: 0 0 100% !important;
  }
  
  .chat-layout-container.mobile-view.has-active-chat .sidebar-glass {
    display: none !important;
  }
  
  .chat-layout-container.mobile-view:not(.has-active-chat) .main-chat-area {
    display: none !important;
  }
}
</style>
